/* eslint-disable @typescript-eslint/no-explicit-any */
import { DropdownButton } from "@/components/Dropdown/Index";
import { SelectOptionType } from "@/components/Radio";
import { DEFAULT_FORMAT_DATE } from "@/constants/common";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { ChangeEvent } from "react";
import { Checkbox, TableTypes } from "tvs-unified-lib";
import * as yup from "yup";
import {
  ACTION,
  CHANNEL,
  DATA_SOURCE_TYPE,
  MAIN_OPERATOR,
  SCHEDULE_MODE,
  YES_NO,
  optionsMOBAction,
} from "./constant";
import { TextCampaignsId } from "./styles";
import { CampaignForm, ConditionItem, DataType, DataTypeList } from "./types";

export const campaignColumns = (
  selected: number[],
  setSelected: (selected: number[]) => void,
  toggleCampaignById: (id: number, checked: boolean) => void,
  handleCopyCampaign: (id: number) => void,
  handleDownloadReport: (id: number) => void,
  handleRedirectEditCampaign: (id: number) => void
): ColumnDef<DataTypeList>[] => [
  {
    header: () => "",
    accessorKey: "action",
    enableSorting: false,
    size: 50,
    cell: ({ row }: TableTypes.CellType<DataTypeList>) => {
      return (
        <DropdownButton
          id="button-3"
          buttonProps={{
            variant: "no-border",
          }}
          menus={[
            {
              label: "Copy",
              onClick: () => {
                handleCopyCampaign(row.original.id);
              },
            },
            {
              label: "Download Report",
              onClick: () => {
                handleDownloadReport(row.original.id);
              },
            },
          ]}
        />
      );
    },
  },
  {
    header: () => "",
    accessorKey: "switch",
    enableSorting: false,
    size: 50,
    cell: ({ row }: TableTypes.CellType<DataTypeList>) => {
      return (
        <Checkbox
          variant="switch"
          formLabel=""
          name={`switch-${row.original.id}`}
          value={selected as any}
          checkboxValues={[
            {
              label: "",
              value: row.original.id as any,
            },
          ]}
          onInputChange={(e: ChangeEvent) => {
            const checked = (e.target as HTMLInputElement).checked;
            toggleCampaignById(row.original.id, checked);
            const ids = [...selected];
            if ((e.target as HTMLInputElement).checked) {
              ids.push(row.original.id);
              setSelected(ids);
            } else {
              const filteredIds = ids.filter((i) => i !== row.original.id);
              setSelected(filteredIds);
            }
          }}
        />
      );
    },
  },
  {
    header: () => "Campaign ID",
    enableSorting: false,
    accessorKey: "campaignId",
    size: 200,
    cell: ({ row }: TableTypes.CellType<DataTypeList>) => {
      return (
        <TextCampaignsId
          onClick={() => handleRedirectEditCampaign(row.original.id)}
        >
          {row.original.campaignId}
        </TextCampaignsId>
      );
    },
  },
  {
    header: () => "Campaign Name",
    enableSorting: false,
    accessorKey: "campaignName",
    size: 200,
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return value;
    },
  },
  {
    header: () => "Included Count",
    enableSorting: false,
    accessorKey: "includedCount",
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return value !== null ? value : "-";
    },
  },
  {
    header: () => "Created By",
    enableSorting: false,
    accessorKey: "createdBy",
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return value ? value.firstName + " " + value.lastName : "-";
    },
  },
  {
    header: () => "Created Date",
    enableSorting: false,
    accessorKey: "createdDate",
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return moment(value).format(DEFAULT_FORMAT_DATE);
    },
  },
  {
    header: () => "Updated By",
    enableSorting: false,
    accessorKey: "updatedBy",
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return value ? value.firstName + " " + value.lastName : "-";
    },
  },
  {
    header: () => "Updated Date",
    enableSorting: false,
    accessorKey: "updatedDate",
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return moment(value).format(DEFAULT_FORMAT_DATE);
    },
  },
  {
    header: () => "Activated By",
    enableSorting: false,
    accessorKey: "activatedBy",
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return value ? value.firstName + " " + value.lastName : "-";
    },
  },
  {
    header: () => "Activated Date",
    enableSorting: false,
    accessorKey: "activatedAt",
    cell: ({ getValue }: TableTypes.CellType<DataTypeList>) => {
      const value = getValue();
      return value ? moment(value).format(DEFAULT_FORMAT_DATE) : "-";
    },
  },
];

// REGION: form validation
const labelValueArraySchema = (requiredMessage: string) =>
  yup
    .array()
    .of(
      yup.object().shape({
        label: yup.string(),
        value: yup.string(),
      })
    )
    .min(1, requiredMessage)
    .required(requiredMessage);

export const schema = yup.object().shape({
  name: yup.string().required("Campaign name is required"),
  status: yup.string().required("Status is required"),
  // TODO: Remove this when the API is ready
  // campaign_id: yup.string().required("Campaign ID is required"),
  start_date: yup.string().required("Start date is required"),
  end_date: yup.string().required("End date is required"),
  data_source: labelValueArraySchema("Data source is required"),
  data_file: yup.object().when("data_source", {
    is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.DATA_UPLOAD,
    then: () => labelValueArraySchema("Data file is required"),
    otherwise: (schema) => schema,
  }),
  repeat: labelValueArraySchema("Repeat is required"),
  repeat_frequency: yup.mixed().when("repeat", {
    is: (val: SelectOptionType[]) => val?.[0]?.value === "true",
    then: () => labelValueArraySchema("Repeat frequency is required"),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  repeat_end: yup.string().when("repeat", {
    is: (val: SelectOptionType[]) => val?.[0]?.value === "true",
    then: (schema) =>
      schema
        .required("End condition for repeat is required")
        .oneOf(
          [
            "repeat_end_never",
            "repeat_end_after_number_of_occurrences",
            "repeat_end_on",
          ],
          "Invalid repeat end condition selected"
        ),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  repeat_end_never: yup.boolean().when(["repeat", "repeat_end"], {
    is: (repeatVal: SelectOptionType[], repeatEndVal: string) =>
      repeatVal?.[0]?.value === "true" && repeatEndVal === "repeat_end_never",
    then: (schema) => schema,
    otherwise: (schema) => schema.optional().nullable(),
  }),
  repeat_end_after_number_of_occurrences: yup
    .number()
    .transform((currentCastedValue, originalValue) => {
      if (originalValue === "" || originalValue == null) {
        return undefined;
      }
      return currentCastedValue;
    })
    .nullable()
    .when(["repeat", "repeat_end"], {
      is: (repeatVal: SelectOptionType[], repeatEndVal: string) =>
        repeatVal?.[0]?.value === "true" &&
        repeatEndVal === "repeat_end_after_number_of_occurrences",
      then: (schema) =>
        schema
          .typeError("Number must be a valid number.")
          .required("Number is required.")
          .integer("Number must be a whole number.")
          .positive("Number must be greater than 0."),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  repeat_end_on: yup.date().when(["repeat", "repeat_end"], {
    is: (repeatVal: SelectOptionType[], repeatEndVal: string) =>
      repeatVal?.[0]?.value === "true" && repeatEndVal === "repeat_end_on",
    then: (schema) =>
      schema.typeError("Invalid date format").required("Date is required"),
    otherwise: () => yup.string().optional().nullable(),
  }),
  remarks: yup.string(),

  // Customer Parameters - Required when data_source is AIDC
  // TODO: Remove this when the API is ready
  // customer_region: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === "AIDC",
  //   then: () => labelValueArraySchema("Customer region is required"),
  //   otherwise: (schema) => schema,
  // }),
  // customer_state: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Customer state is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // customer_city: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Customer city is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // customer_area: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Customer area is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // customer_pincode: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Customer pincode is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // occupation: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Occupation is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // age_band: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Age band is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // customer_risk_profile: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Customer risk profile is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // channel_propensity: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Channel propensity is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // propensity_to_pay: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Propensity to pay is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // paid_last_three_months: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Paid last three months is required"),
  //   otherwise: () => yup.object().optional().nullable(),
  // }),
  // online_paid_ever: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Online paid ever is required"),
  //   otherwise: () => yup.object().optional().nullable(),
  // }),
  // current_month_bounce: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Current month bounce is required"),
  //   otherwise: () => yup.string().optional().nullable(),
  // }),

  // Loan Parameters - Required when data_source is AIDC
  // account_type: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Account type is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // mob: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("MOB is required"),
  //   otherwise: () => yup.object().optional().nullable(),
  // }),
  // bucket: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Bucket is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // portfolio: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Portfolio is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // overdue_amount_band: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Overdue amount band is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // value_at_risks: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Value at risks is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // first_emi: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("First EMI is required"),
  //   otherwise: () => yup.object().optional().nullable(),
  // }),
  // second_emi: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Second EMI is required"),
  //   otherwise: () => yup.object().optional().nullable(),
  // }),
  // third_emi: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Third EMI is required"),
  //   otherwise: () => yup.object().optional().nullable(),
  // }),
  // dealer_branch: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Dealer branch is required"),
  //   otherwise: () => yup.array().optional().nullable(),
  // }),
  // TODO: Remove this when the API is ready
  // dealer_pincode: yup.array().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === "AIDC",
  //   then: () => labelValueArraySchema("Dealer pincode is required"),
  //   otherwise: (schema) => schema,
  // }),
  // payment_frequency: yup.object().when("data_source", {
  //   is: (val: any) => val?.[0]?.value === DATA_SOURCE_TYPE.AIDC,
  //   then: () => labelValueArraySchema("Payment frequency is required"),
  //   otherwise: () => yup.object().optional().nullable(),
  // }),

  // Campaign Channels - Always required
  campaign_channels: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          channel: yup.string().required("Channel is required"),
          trigger_by: yup.array().when("channel", {
            is: (channel: string) => channel !== CHANNEL.FIELD,
            then: () => labelValueArraySchema("Trigger by is required"),
            otherwise: (schema) => schema.optional().nullable(),
          }),

          condition: yup.array().when("channel", {
            is: (channel: string) => channel !== CHANNEL.FIELD,
            then: () => labelValueArraySchema("Condition is required"),
            otherwise: (schema) => schema.optional().nullable(),
          }),

          action: yup.array().when("channel", {
            is: (channel: string) => channel !== CHANNEL.FIELD,
            then: () => labelValueArraySchema("Action is required"),
            otherwise: (schema) => schema.optional().nullable(),
          }),

          is_check_day: yup.string().when(["channel", "action"], {
            is: (channel: string, action: SelectOptionType[]) =>
              channel !== CHANNEL.FIELD &&
              action?.[0]?.value !== ACTION.ON_DATE,
            then: () => yup.string().required("Specific day check is required"),
            otherwise: (schema) => schema,
          }),

          allocation_limit: yup
            .number()
            .transform((value, originalValue) =>
              originalValue === "" ? null : value
            )
            .typeError("Allocation limit must be a number")
            .max(120, "Allocation limit cannot exceed 120")
            .nullable()
            .optional(),

          acr: yup
            .number()
            .transform((value, originalValue) =>
              originalValue === "" ? null : value
            )
            .typeError("ACR must be a number")
            .integer("ACR must be a whole number")
            .positive("ACR must be a positive number")
            .max(999, "ACR cannot exceed 999")
            .nullable()
            .optional(),

          km_range: yup
            .number()
            .transform((value, originalValue) =>
              originalValue === "" ? null : value
            )
            .typeError("KM Range must be a number")
            .integer("KM Range must be a whole number")
            .positive("KM Range must be a positive number")
            .max(999, "KM Range cannot exceed 999")
            .nullable()
            .optional(),

          // TODO: Don't need validation for specific_day_check and number_of_day_check because it's already validated in the transform
          // specific_day_check: yup.mixed().when("is_check_day", {
          //   is: (val: string) => val === SCHEDULE_MODE.SPECIFIC_DATE,
          //   then: () => labelValueArraySchema("Specific day check is required"),
          //   otherwise: (schema) => schema,
          // }),
          // number_of_day_check: yup.mixed().when("is_check_day", {
          //   is: (val: string) => val === SCHEDULE_MODE.NUMBER_OF_DAYS,
          //   then: () => labelValueArraySchema("Number of days check is required"),
          //   otherwise: (schema) => schema,
          // }),
          // TODO: Don't need validation for main_operator because it's already validated in the transform
          main_operator: yup.string().optional().nullable(),
          campaign_data_channels: yup.array().of(
            yup.object().shape({
              // Add a field to hold the injected value, make it optional as it's for validation
              _injected_channel: yup.string().optional(),
              _injected_is_check_day: yup.string().optional(),
              _injected_action: yup.string().optional(),
              day: yup
                .array()
                .when(
                  [
                    "_injected_channel",
                    "_injected_is_check_day",
                    "_injected_action",
                  ],
                  {
                    // Validate against the injected value
                    is: (
                      channel: string,
                      is_check_day: string,
                      action: string
                    ) => {
                      if (channel === CHANNEL.FIELD) {
                        return false;
                      }
                      return (
                        is_check_day === SCHEDULE_MODE.SPECIFIC_DATE &&
                        action !== ACTION.ON_DATE
                      );
                    },
                    then: () => labelValueArraySchema("Day is required"),
                    otherwise: (schema) => schema.optional().nullable(),
                  }
                ),
              number_of_days: yup
                .number()
                .when(
                  [
                    "_injected_channel",
                    "_injected_is_check_day",
                    "_injected_action",
                    "start_date",
                    "end_date",
                  ],
                  {
                    is: (
                      channel: string,
                      is_check_day: string,
                      action: string
                    ) => {
                      if (channel === CHANNEL.FIELD) {
                        return false;
                      }
                      return (
                        is_check_day === SCHEDULE_MODE.NUMBER_OF_DAYS &&
                        action !== ACTION.ON_DATE
                      );
                    },
                    then: (schema) =>
                      schema
                        .typeError("Number of days must be a valid number.")
                        .integer("Number of days must be a whole number.")
                        .positive("Number of days must be a positive number.")
                        .test(
                          "max-days",
                          "The number of days exceeds the campaign duration.",
                          function (value) {
                            // this.from is an array of all the form values
                            const formValues = this.from && this.from[2]?.value;
                            if (
                              !value ||
                              !formValues.start_date ||
                              !formValues.end_date
                            )
                              return true;

                            const start = new Date(formValues.start_date);
                            const end = new Date(formValues.end_date);
                            const diffTime = Math.abs(
                              end.getTime() - start.getTime()
                            );
                            const diffDays = Math.ceil(
                              diffTime / (1000 * 60 * 60 * 24)
                            );

                            return value <= diffDays;
                          }
                        )
                        .required(
                          "Number of days is required when schedule is by number of days."
                        ),
                    otherwise: (schema) => schema.optional().nullable(),
                  }
                ),
              time_band: yup.array().when("_injected_channel", {
                is: (val: any) => val !== CHANNEL.FIELD,
                then: () => labelValueArraySchema("Time band is required"),
                otherwise: (schema) => schema.optional().nullable(),
              }),
              template_script_id: yup.array().when("_injected_channel", {
                is: (val: any) => val !== CHANNEL.FIELD,
                then: () =>
                  labelValueArraySchema("Template script ID is required"),
                otherwise: (schema) => schema.optional().nullable(),
              }),
            })
          ),
          refer_out: yup
            .array()
            .of(
              yup.object({
                when: yup.array().min(1, "When is required"),
                condition: yup.array().min(1, "Condition is required"),
                action_type_numeric: yup
                  .number()
                  .transform((val, orig) => (orig === "" ? undefined : val))
                  .when(["action_validation_type", "action_is_disabled"], {
                    is: (displayType: string, disabled: any) => {
                      return displayType === "numeric" && !disabled;
                    },
                    then: (schema) => schema.required("Action is required"),
                    otherwise: (schema) => schema.notRequired().nullable(),
                  }),

                action_type_date: yup
                  .date()
                  .transform((val, orig) => (orig === "" ? undefined : val))
                  .when(["action_validation_type", "action_is_disabled"], {
                    is: (displayType: string, disabled: any) => {
                      return displayType === "date" && !disabled;
                    },
                    then: (schema) => schema.required("Action is required"),
                    otherwise: (schema) => schema.notRequired().nullable(),
                  }),

                action_type_string: yup
                  .string()
                  .nullable()
                  .when(["action_validation_type", "action_is_disabled"], {
                    is: (type: string, disabled: any) =>
                      type === "string" && !disabled,
                    then: (schema) => schema.required("Action is required"),
                    otherwise: (schema) => schema.notRequired().nullable(),
                  }),

                action_type_dropdown: yup
                  .array()
                  .of(yup.object())
                  .when(["action_display_type", "action_is_disabled"], {
                    is: (displayType: string, disabled: any) => {
                      return displayType === "dropdown" && !disabled;
                    },
                    then: (schema) => schema.min(1, "Action is required"),
                    otherwise: (schema) => schema.notRequired().nullable(),
                  }),

                action_type_boolean: yup
                  .array()
                  .of(yup.object())
                  .when(["action_validation_type", "action_is_disabled"], {
                    is: (type: string, disabled: any) =>
                      type === "boolean" && !disabled,
                    then: (schema) => schema.min(1, "Action is required"),
                    otherwise: (schema) => schema.notRequired().nullable(),
                  }),
              })
            )
            .when("main_operator", {
              is: "OR",
              then: (schema) =>
                schema.min(1, "At least one Refer Out is required"),
              otherwise: (schema) => schema.strip().notRequired(),
            }),

          // Additional Parameters: optional overall, but when present each group and condition must be valid
          additional_parameters: yup
            .object()
            .shape({
              ops: yup
                .array()
                .of(yup.string().oneOf(["AND", "OR"]))
                .optional()
                .nullable(),
              nodes: yup
                .array()
                .of(
                  yup
                    .object()
                    .shape({
                      field: yup.string().optional().nullable(),
                      ops: yup
                        .array()
                        .of(yup.string().oneOf(["AND", "OR"]))
                        .optional()
                        .nullable(),
                      nodes: yup
                        .array()
                        .of(
                          yup.object().shape({
                            field: yup.string().required("Field is required"),
                            op: yup.string().required("Operator is required"),
                            value: yup.mixed().test(
                              "additional-param-value",
                              "Invalid value for selected operator",
                              function (val) {
                                const op = this.parent?.op;
                                if (!op) return true; // can't validate without operator

                                if (op === "BETWEEN") {
                                  if (!Array.isArray(val) || val.length < 2) {
                                    return this.createError({ message: "Both values are required for BETWEEN" });
                                  }
                                  const [a, b] = val;
                                  if (
                                    a == null ||
                                    b == null ||
                                    String(a).trim() === "" ||
                                    String(b).trim() === ""
                                  ) {
                                    return this.createError({ message: "Both values are required for BETWEEN" });
                                  }
                                  return true;
                                }

                                if (op === "BEFORE" || op === "AFTER") {
                                  if (!val || String(val).trim() === "") {
                                    return this.createError({ message: "Date is required" });
                                  }
                                  return true;
                                }

                                // default single-value operators
                                if (val == null || String(val).trim() === "") {
                                  return this.createError({ message: "Value is required" });
                                }
                                return true;
                              }
                            ),
                          })
                        )
                        .min(1, "At least one condition is required")
                        .required(),
                    })
                )
                .optional()
                .nullable(),
            })
            .optional()
            .nullable(),
        })
        // Transform each campaign_channel object
        // How it works:
        // - The .transform() is applied to each object within the campaign_channels array.
        // - Inside the transform, we take the is_check_day from the current channel.
        // - We then map over its campaign_data_channels and add a new property (e.g., _injected_is_check_day) to each item, copying the parent's is_check_day value.
        // - The when conditions inside campaign_data_channels now refer to this new _injected_is_check_day property.
        // Caveats for transform:
        // - Transforms run early in the validation process. The value of is_check_day used in the transform will be its value before its own validation rules (like required) have been fully processed for that specific channel object. This is usually fine if it's already set by the form.
        // - The transformed data is what's used for subsequent validation, but the original form data is typically what React Hook Form keeps. This can sometimes lead to discrepancies if not handled carefully, though for validation purposes, it might be acceptable.
        // - It adds a layer of complexity to the schema.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .transform((currentChannelValue, originalChannelValue) => {
          if (
            currentChannelValue &&
            currentChannelValue.campaign_data_channels
          ) {
            const channel = currentChannelValue.channel;
            const checkDay = currentChannelValue.is_check_day;
            const checkAction = currentChannelValue.action;
            const transformedDataChannels =
              currentChannelValue.campaign_data_channels.map((dc: any) => ({
                ...dc,
                _injected_channel: channel,
                _injected_is_check_day: checkDay,
                _injected_action: checkAction ? checkAction[0]?.value : "",
              }));
            return {
              ...currentChannelValue,
              campaign_data_channels: transformedDataChannels,
            };
          }
          return currentChannelValue; // Or originalChannelValue
        })
    )
    .min(1, "At least one campaign channel is required")
    .required("Campaign channels are required"),
});
// END REGION: form validation

// REGION: transform payload
const convertBucketWiseLoadBalancing = (
  value: string | undefined
): boolean | null => {
  if (value === YES_NO.YES) return true;
  if (value === YES_NO.NO) return false;
  return null;
};

const mapFormValidationTypeToApiType = (
  validationType: ConditionItem["action_validation_type"]
): string => {
  switch (validationType) {
    case "numeric":
      return "Float";
    case "boolean":
      return "Boolean";
    case "date":
      return "Date";
    case "string":
    default:
      return "String";
  }
};

const transformReferOutItemForApi = (
  formConditionItem: ConditionItem
): ApiConditionObject => {
  const apiType = mapFormValidationTypeToApiType(
    formConditionItem.action_validation_type
  );

  let actionValue = "";
  if (
    formConditionItem.action_display_type === ActionFieldDisplayTypes.Dropdown
  ) {
    actionValue = String(
      formConditionItem.action_type_dropdown?.[0]?.value ||
        (formConditionItem.action as SelectOptionType)?.value ||
        ""
    );
  } else if (formConditionItem.action_validation_type === "numeric") {
    actionValue = String(
      formConditionItem.action_type_numeric || formConditionItem.action || ""
    );
  } else if (formConditionItem.action_validation_type === "date") {
    actionValue = String(
      formConditionItem.action_type_date || formConditionItem.action || ""
    );
  } else if (formConditionItem.action_validation_type === "boolean") {
    actionValue = String(
      formConditionItem.action_type_boolean?.[0]?.value ||
        (formConditionItem.action as SelectOptionType)?.value ||
        ""
    );
  } else {
    actionValue = String(
      formConditionItem.action_type_string || formConditionItem.action || ""
    );
  }

  return {
    type: apiType,
    when:
      Array.isArray(formConditionItem?.when) &&
      formConditionItem?.when.length > 0
        ? formConditionItem?.when[0]?.value
        : formConditionItem?.when,
    condition:
      Array.isArray(formConditionItem?.condition) &&
      formConditionItem?.condition.length > 0
        ? formConditionItem?.condition[0]?.value
        : formConditionItem?.condition,
    action: actionValue,
  };
};

export const groupConditionsForApi = (
  flatFormConditions: ConditionItem[] | undefined
): ApiReferOutData => {
  if (!flatFormConditions || flatFormConditions.length === 0) {
    return [];
  }

  const transformedForApi = flatFormConditions.map(transformReferOutItemForApi);

  const groupedResult: ApiConditionObject[][] = [];
  let currentAndGroup: ApiConditionObject[] = [];

  for (let i = 0; i < transformedForApi.length; i++) {
    const item = transformedForApi[i];
    const formItemOriginal = flatFormConditions[i];

    currentAndGroup.push(item);

    if (
      formItemOriginal.current_operator === "OR" ||
      i === transformedForApi.length - 1
    ) {
      if (currentAndGroup.length > 0) {
        groupedResult.push(currentAndGroup);
      }
      currentAndGroup = [];
    }
  }

  return groupedResult;
};
// END REGION: transform payload

// REGION: transform api refer out to form
interface ApiConditionObject {
  type: string;
  when: string;
  condition: string;
  action: string;
}

type ApiReferOutData = ApiConditionObject[][];

export enum ActionFieldDisplayTypes {
  Input = "input",
  Dropdown = "dropdown",
  Disabled = "disabled",
}

export type AcceptableValidationTypes =
  | "numeric"
  | "date"
  | "string"
  | "boolean";

const findOption = (
  value: string | number | boolean | null | undefined,
  options: SelectOptionType[],
  labelOverride?: string
): SelectOptionType | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const found = options.find((opt) => opt.value === value);
  return (
    found ||
    ({
      label: labelOverride || String(value),
      value: String(value),
    } as SelectOptionType)
  );
};

interface DerivedReferOutProperties {
  condition_options: SelectOptionType[];
  action_display_type: ActionFieldDisplayTypes | string;
  action_validation_type: AcceptableValidationTypes | string;
  action_options: SelectOptionType[];
  action_placeholder: string;
  action_is_disabled: boolean;
}

export const getDerivedStateFromWhenValue = (
  selectedWhenValue: string | number | boolean | null | undefined,
  optionsForSwitch: {
    NUMERIC_COMPARISON_OPTIONS: SelectOptionType[];
    PRIORITY_LEVEL_OPTIONS: SelectOptionType[];
    BOOLEAN_CHOICE_OPTIONS: SelectOptionType[];
    EQUALITY_COMPARISON_OPTIONS: SelectOptionType[];
  }
): DerivedReferOutProperties => {
  let newConditionOptions: SelectOptionType[] = [];
  let newActionDisplayType: ActionFieldDisplayTypes | string =
    ActionFieldDisplayTypes.Disabled;
  let newActionValidationType: AcceptableValidationTypes | string = "string";
  let newActionOptions: SelectOptionType[] = [];
  let newActionPlaceholder: string = "Action (Disabled)";
  let newActionIsDisabled: boolean = true;

  const {
    NUMERIC_COMPARISON_OPTIONS,
    PRIORITY_LEVEL_OPTIONS,
    BOOLEAN_CHOICE_OPTIONS,
    EQUALITY_COMPARISON_OPTIONS,
  } = optionsForSwitch;

  switch (selectedWhenValue) {
    case "Bucket":
      newConditionOptions = NUMERIC_COMPARISON_OPTIONS;
      newActionDisplayType = ActionFieldDisplayTypes.Input;
      newActionValidationType = "numeric";
      newActionPlaceholder = "Action";
      newActionIsDisabled = false;
      break;
    case "Past Due Date":
      newConditionOptions = NUMERIC_COMPARISON_OPTIONS;
      newActionDisplayType = ActionFieldDisplayTypes.Input;
      newActionValidationType = "date";
      newActionPlaceholder = "Action";
      newActionIsDisabled = false;
      break;
    case "Customer Risk Factor":
      newConditionOptions = PRIORITY_LEVEL_OPTIONS;
      newActionDisplayType = ActionFieldDisplayTypes.Input;
      newActionValidationType = "string";
      newActionPlaceholder = "Action";
      newActionIsDisabled = true;
      break;
    case "Current Month Bounce":
      newConditionOptions = BOOLEAN_CHOICE_OPTIONS;
      newActionDisplayType = ActionFieldDisplayTypes.Input;
      newActionValidationType = "string";
      newActionPlaceholder = "Action";
      newActionIsDisabled = true;
      break;
    case "Overdue Amount":
      newConditionOptions = NUMERIC_COMPARISON_OPTIONS;
      newActionDisplayType = ActionFieldDisplayTypes.Input;
      newActionValidationType = "numeric";
      newActionPlaceholder = "Action";
      newActionIsDisabled = false;
      break;
    case "MOB":
      newConditionOptions = EQUALITY_COMPARISON_OPTIONS;
      newActionDisplayType = ActionFieldDisplayTypes.Dropdown;
      newActionValidationType = "numeric";
      newActionOptions = optionsMOBAction;
      newActionPlaceholder = "Action";
      newActionIsDisabled = false;
      break;
    case "First EMI":
    case "Second EMI":
      newConditionOptions = BOOLEAN_CHOICE_OPTIONS;
      newActionDisplayType = ActionFieldDisplayTypes.Dropdown;
      newActionValidationType = "string";
      newActionPlaceholder = "Action";
      break;
    default:
      break;
  }
  return {
    condition_options: newConditionOptions,
    action_display_type: newActionDisplayType,
    action_validation_type: newActionValidationType,
    action_options: newActionOptions,
    action_placeholder: newActionPlaceholder,
    action_is_disabled: newActionIsDisabled,
  };
};

interface TransformReferOutOptions {
  optionsWhenMasterList: SelectOptionType[];
  derivedStateOptions: Parameters<typeof getDerivedStateFromWhenValue>[1];
}

export const transformApiReferOutToForm = (
  apiReferOutGroups: ApiReferOutData | undefined | null,
  transformOptions: TransformReferOutOptions
): ConditionItem[] => {
  if (!apiReferOutGroups || apiReferOutGroups.length === 0) {
    return [];
  }

  const { optionsWhenMasterList, derivedStateOptions } = transformOptions;
  const flatFormConditions: ConditionItem[] = [];

  apiReferOutGroups.forEach((andGroup, orGroupIndex) => {
    andGroup.forEach((apiCondition, andItemIndex) => {
      const selectedWhenOption = findOption(
        apiCondition.when,
        optionsWhenMasterList
      );
      const derived = getDerivedStateFromWhenValue(
        selectedWhenOption?.value,
        derivedStateOptions
      );
      const selectedConditionOption = findOption(
        apiCondition.condition,
        derived.condition_options
      );

      const formItem: ConditionItem = {
        when: selectedWhenOption,
        condition: selectedConditionOption,
        action: undefined,

        condition_options: derived.condition_options,
        action_display_type: derived.action_display_type,
        action_validation_type: derived.action_validation_type,
        action_options: derived.action_options,
        action_placeholder: derived.action_placeholder,
        action_is_disabled: derived.action_is_disabled,

        action_type_numeric: undefined,
        action_type_boolean: undefined,
        action_type_string: undefined,
        action_type_date: undefined,
        action_type_dropdown: undefined,

        current_operator: undefined,
      };

      const apiActionValue = apiCondition.action;

      switch (derived.action_display_type) {
        case ActionFieldDisplayTypes.Input:
          if (derived.action_validation_type === "numeric") {
            formItem.action_type_numeric = apiActionValue;
            formItem.action = apiActionValue;
          } else if (derived.action_validation_type === "date") {
            formItem.action_type_date = apiActionValue;
            formItem.action = apiActionValue;
          } else {
            formItem.action_type_string = apiActionValue;
            formItem.action = apiActionValue;
          }
          break;
        case ActionFieldDisplayTypes.Dropdown:
          // eslint-disable-next-line no-case-declarations
          const selectedActionOption = findOption(
            apiActionValue,
            derived.action_options,
            apiActionValue
          );
          formItem.action_type_dropdown = [
            selectedActionOption || ({} as SelectOptionType),
          ];
          formItem.action = selectedActionOption;
          break;
        default:
          formItem.action_type_string = apiActionValue;
          formItem.action = apiActionValue;
          break;
      }

      if (andItemIndex < andGroup.length - 1) {
        formItem.current_operator = "AND";
      } else if (orGroupIndex < apiReferOutGroups.length - 1) {
        formItem.current_operator = "OR";
      }

      flatFormConditions.push(formItem);
    });
  });

  return flatFormConditions;
};
// END REGION: transform api refer out to form

// REGION: create payload
export const createPayload = (data: CampaignForm): DataType => {
  return {
    name: data.name || "",
    status: data.status || "DRAFT",
    start_date: data.start_date || "",
    end_date: data.end_date || "",
    data_source: data.data_source?.[0]?.value || "",
    ...(data.data_source?.[0]?.value === DATA_SOURCE_TYPE.DATA_UPLOAD && {
      bulk_upload_history_id: data.data_file?.[0]?.value || "",
      data_file: data.data_file?.[0]?.value || "",
    }),
    repeat: data.repeat?.[0]?.value === "true",
    ...(data.repeat?.[0]?.value === "true" && {
      repeat_end: data.repeat_end || "",
      repeat_frequency: data.repeat_frequency?.[0]?.value.toUpperCase() || "",
      ...(data.repeat_end_never &&
        data.repeat_end === "repeat_end_never" && {
          repeat_end_never: data.repeat_end_never,
        }),
      ...(data.repeat_end_after_number_of_occurrences &&
        data.repeat_end === "repeat_end_after_number_of_occurrences" && {
          repeat_end_after_number_of_occurrences:
            data.repeat_end_after_number_of_occurrences || "",
        }),
      ...(data.repeat_end_on &&
        data.repeat_end === "repeat_end_on" && {
          repeat_end_on: data.repeat_end_on || "",
        }),
    }),
    remarks: data.remarks || "",
    campaign_channels:
      data.campaign_channels?.map((channel) => ({
        channel: channel.channel || "",
        trigger_by: channel.trigger_by?.[0]?.value || "",
        condition: channel.condition?.[0]?.value || "",
        action: channel.action?.[0]?.value || "",
        specific_day_check:
          channel.is_check_day === SCHEDULE_MODE.SPECIFIC_DATE,
        number_of_day_check:
          channel.is_check_day === SCHEDULE_MODE.NUMBER_OF_DAYS,
        // include additional_parameters built by AdditionalParameterForm (fallback to legacy additional_parameter if present)
        additional_parameters:
          (channel as any).additional_parameters ?? { ops: [], nodes: [] },
        main_operator: channel.main_operator || null,

        // for allocation
        ...(channel.channel === CHANNEL.FIELD && {
          previous_month_allocation: channel.previous_month_allocation || false,
          collection_success: channel.collection_success || false,
          collection_failure: channel.collection_failure || false,
          allocation_status: channel?.allocation_status || [],
          allocation_limit: channel.allocation_limit || null,

          portfolio_category_id: channel?.portfolio?.[0] || null,
          state_id: channel?.location?.[0]?.data?.state?.id || null,
          district_id: channel?.location?.[0]?.data?.city?.id || null,
          area_id: channel.location?.[0]?.data?.area?.id || null,
          pincode_id: channel.location?.[0]?.data?.pincode?.id || null,

          bucket_wise_load_balancing: convertBucketWiseLoadBalancing(
            channel.bucket_wise_load_balancing
          ),
          km_range: channel.km_range || null,
          acr: channel.acr || null,
          collector_segment:
            channel.collector_segment?.map((item) => item.value) || [],
          collector_gender: channel.collector_gender?.[0]?.value || null,
          performance_rating:
            channel.performance_rating?.map((item) => item.value) || [],
          itinerary: channel.itinerary?.map((item) => item.value) || [],
        }),

        // if channel is not FIELD, then add campaign_data_channels
        ...(channel.channel !== CHANNEL.FIELD && {
          campaign_data_channels:
            channel.campaign_data_channels?.map((dataChannel) => ({
              day: dataChannel.day?.map((item) => item.value) || undefined,
              number_of_days: dataChannel.number_of_days
                ? parseInt(dataChannel.number_of_days.toString())
                : undefined,
              time_band: dataChannel.time_band.map((item) => item.value) || [],
              template_script_id: dataChannel.template_script_id?.[0]?.value
                ? parseInt(dataChannel.template_script_id[0].value)
                : 0,
            })) || [],
        }),

        // REGION: refer out
        ...(channel.main_operator === MAIN_OPERATOR.AND && {
          refer_out: [],
        }),
        ...(channel.main_operator === MAIN_OPERATOR.OR && {
          refer_out: groupConditionsForApi(channel.refer_out || []).map(
            (referOut: ConditionItem[]) => {
              return referOut.map((item) => ({
                type: item.type || "",
                when: item.when || "",
                condition: item.condition || "",
                action: item.action || "",
              }));
            }
          ) as {
            type: string;
            when: string;
            condition: string;
            action: string | number | boolean | null;
          }[][],
        }),
      })) || [],
    // END REGION: refer out

    // apply condition when data_source is AIDC
    ...(data.data_source?.[0]?.value === DATA_SOURCE_TYPE.AIDC && {
      customer_params: {
        state_ids:
          data.customer_state
            ?.map((item: SelectOptionType) => parseInt(item.value, 10))
            .filter((value): value is number => !isNaN(value)) || [],
        district_ids:
          data.customer_city
            ?.map((item: SelectOptionType) => parseInt(item.value, 10))
            .filter((value): value is number => !isNaN(value)) || [],
        area_ids:
          data.customer_area
            ?.map((item: SelectOptionType) => parseInt(item.value, 10))
            .filter((value): value is number => !isNaN(value)) || [],
        pincode_ids:
          data.customer_pincode
            ?.map((item: SelectOptionType) => parseInt(item.value, 10))
            .filter((value): value is number => !isNaN(value)) || [],

        occupation: data.occupation?.map((item) => item.value) || [],
        age_band: data.age_band?.map((item) => item.value) || [],
        customer_risk_profile:
          data.customer_risk_profile?.map((item) => item.value) || [],
        channel_propensity:
          data.channel_propensity?.map((item) => item.value) || [],
        propensity_to_pay:
          data.propensity_to_pay?.map((item) => item.value) || [],
        paid_last_three_months: data.paid_last_three_months?.[0]?.value || "",
        online_paid_ever: data.online_paid_ever?.[0]?.value || "",
        current_month_bounce: data.current_month_bounce?.[0]?.value || "",
      },
      loan_params: {
        account_type: data.account_type?.map((item) => item.value) || [],
        mob: data.mob?.[0]?.value || "",
        bucket: data.bucket?.map((item) => item.value) || [],
        portfolio:
          data.portfolio
            ?.map((item: SelectOptionType) => parseInt(item.value, 10))
            .filter((value): value is number => !isNaN(value)) || [],
        overdue_amount_band:
          data.overdue_amount_band?.map((item) => item.value) || [],
        value_at_risks: data.value_at_risks?.map((item) => item.value) || [],
        first_emi: data.first_emi?.[0]?.value || "",
        second_emi: data.second_emi?.[0]?.value || "",
        third_emi: data.third_emi?.[0]?.value || "",
        dealer_branch: data.dealer_branch?.map((item) => item.value) || [],
        payment_frequency: data.payment_frequency?.[0]?.value || "",
      },
    }),
  };
};
// END REGION: create payload

// REGION: edit payload
export const editPayload = (data: CampaignForm): DataType => {
  const payload = createPayload(data);
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data_source,
    // if data_source is DATA_UPLOAD, then need remove bulk_upload_history_id, data_file
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bulk_upload_history_id,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data_file,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    start_date,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    end_date,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    customer_params,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loan_params,
    ...rest
  } = payload;
  return rest;
};
// END REGION: edit payload
